import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { GroupsOutlined, SaveOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";

const AffiliateAdmin = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [overview, setOverview] = React.useState(null);
  const [settings, setSettings] = React.useState({
    enabled: true,
    signup_credits: 25,
    referee_bonus: 0,
  });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/affiliate/admin/overview",
      admin: true,
      post: false,
    });
    if (res?.data?.success) {
      setOverview(res.data.data);
      if (res.data.data?.settings) {
        setSettings({
          enabled: !!res.data.data.settings.enabled,
          signup_credits: Number(res.data.data.settings.signup_credits) || 0,
          referee_bonus: Number(res.data.data.settings.referee_bonus) || 0,
        });
      }
    }
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    const res = await hitAxios({
      path: "/api/affiliate/admin/settings",
      admin: true,
      post: true,
      obj: settings,
    });
    setSaving(false);
    if (res?.data?.success) load();
  };

  const totals = overview?.totals || { invites: 0, credits_paid: 0 };

  return (
    <Box>
      <PageHeader
        title={lang?.affiliateAdminTitle || "Affiliate program"}
        subtitle={
          lang?.affiliateAdminSub ||
          "Referral rewards, top affiliates, and program settings"
        }
        icon={GroupsOutlined}
        primaryAction={null}
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        {[
          { label: "Total invites", value: totals.invites },
          { label: "Credits paid out", value: totals.credits_paid },
          {
            label: "Reward / signup",
            value: `+${settings.signup_credits}`,
          },
        ].map((s) => (
          <Card
            key={s.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
            <Typography variant="h5" fontWeight={800} mt={0.5}>
              {s.value}
            </Typography>
          </Card>
        ))}
      </Stack>

      <Card
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={800} mb={2}>
          Program settings
        </Typography>
        <Stack spacing={2} maxWidth={420}>
          <FormControlLabel
            control={
              <Switch
                checked={!!settings.enabled}
                onChange={(e) =>
                  setSettings((p) => ({ ...p, enabled: e.target.checked }))
                }
              />
            }
            label={settings.enabled ? "Program enabled" : "Program paused"}
          />
          <TextField
            label="Credits to referrer per signup"
            type="number"
            size="small"
            value={settings.signup_credits}
            onChange={(e) =>
              setSettings((p) => ({
                ...p,
                signup_credits: Number(e.target.value),
              }))
            }
            inputProps={{ min: 0, max: 100000 }}
            helperText="Granted when a new user registers via an invite link"
          />
          <TextField
            label="Bonus credits for invited user"
            type="number"
            size="small"
            value={settings.referee_bonus}
            onChange={(e) =>
              setSettings((p) => ({
                ...p,
                referee_bonus: Number(e.target.value),
              }))
            }
            inputProps={{ min: 0, max: 100000 }}
            helperText="Optional — on top of normal signup credits (0 = off)"
          />
          <LoadingButton
            variant="contained"
            loading={saving}
            startIcon={<SaveOutlined />}
            onClick={saveSettings}
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
          >
            Save settings
          </LoadingButton>
        </Stack>
      </Card>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Top affiliates
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Invites</TableCell>
                  <TableCell align="right">Credits</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(overview?.top_affiliates || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        No referrals yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (overview.top_affiliates || []).map((row) => (
                    <TableRow key={row.referrer_uid} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.referrer_name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.referrer_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.referral_code || "—"} />
                      </TableCell>
                      <TableCell align="right">{row.invites}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {row.credits}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1.4,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: alpha(theme.palette.success.main, 0.06),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Recent referrals
            </Typography>
            <Button size="small" onClick={load} sx={{ textTransform: "none" }}>
              Refresh
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Invitee</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(overview?.recent || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        No referral events yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (overview.recent || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.referred_email || row.referred_uid}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.referral_code} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        +{row.credits_awarded}
                      </TableCell>
                      <TableCell>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    </Box>
  );
};

export default AffiliateAdmin;
